import { useState, type FormEvent } from 'react';
import {
  Button,
  ConfirmDialog,
  Input,
  Modal,
  PageHeader,
  RowMenu,
  StatusBadge,
  Switch,
} from '../../components';
import { ApiError } from '../../lib/api';
import { useCrud } from '../../lib/useCrud';

type Source = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  color: string | null;
  is_active: boolean;
};

type FormState = {
  slug: string;
  name: string;
  description: string;
  color: string;
  is_active: boolean;
};
const toForm = (r?: Source): FormState => ({
  slug: r?.slug ?? '',
  name: r?.name ?? '',
  description: r?.description ?? '',
  color: r?.color ?? '#6b7280',
  is_active: r?.is_active ?? true,
});
const toBody = (f: FormState) => ({
  slug: f.slug.trim(),
  name: f.name.trim(),
  description: f.description.trim() || null,
  color: f.color.trim() || null,
  is_active: f.is_active,
});

export function SourcesPage() {
  const crud = useCrud<Source, ReturnType<typeof toBody>, Partial<ReturnType<typeof toBody>>>({
    key: 'sources',
    path: '/settings/sources',
    listQuery: 'include_inactive=true&limit=100',
  });
  const [editing, setEditing] = useState<Source | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Source | null>(null);

  return (
    <>
      <PageHeader
        crumb="Settings"
        title="Sources"
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New source
          </Button>
        }
      />
      {crud.list.isLoading && <div className="card card-body muted">Loading…</div>}
      {crud.list.error && <div className="alert">{(crud.list.error as Error).message}</div>}
      {crud.list.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Color</th>
                <th>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {crud.list.data.data.map((row, i) => (
                <tr key={row.id}>
                  <td className="col-num">{i + 1}</td>
                  <td>{row.name}</td>
                  <td className="cell-mono">{row.slug}</td>
                  <td>
                    {row.color ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          aria-hidden="true"
                          style={{
                            display: 'inline-block',
                            width: 14,
                            height: 14,
                            borderRadius: 4,
                            border: '1px solid var(--border)',
                            background: row.color,
                          }}
                        />
                        <span className="cell-mono">{row.color}</span>
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <StatusBadge active={row.is_active} />
                  </td>
                  <td>
                    <RowMenu
                      items={[
                        { icon: 'edit', label: 'Edit', onClick: () => setEditing(row) },
                        'divider',
                        {
                          icon: 'trash',
                          label: 'Delete',
                          onClick: () => setDeleting(row),
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          row={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSubmit={async (body) => {
            if (editing === 'new') await crud.create.mutateAsync(body);
            else await crud.update.mutateAsync({ id: editing.id, body });
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete source"
        message={deleting ? `Delete "${deleting.name}"?` : null}
        destructive
        confirmLabel="Delete"
        loading={crud.remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await crud.remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

function EditModal({
  row,
  onClose,
  onSubmit,
}: {
  row?: Source;
  onClose: () => void;
  onSubmit: (b: ReturnType<typeof toBody>) => Promise<void>;
}) {
  const [form, setForm] = useState(() => toForm(row));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(toBody(form));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit source' : 'New source'} width={500}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            id="src-name"
            label="Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            autoFocus
          />
          <Input
            id="src-slug"
            label="Slug"
            value={form.slug}
            onChange={(v) => setForm({ ...form, slug: v })}
            required
          />
          <div>
            <label className="field-label" htmlFor="src-desc">
              Description
            </label>
            <textarea
              id="src-desc"
              className="input"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="src-color">
              Color
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                id="src-color"
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                style={{
                  width: 44,
                  height: 36,
                  border: '1px solid var(--border-strong)',
                  borderRadius: 'var(--radius)',
                  background: 'var(--surface)',
                  padding: 2,
                }}
              />
              <Input
                value={form.color}
                onChange={(v) => setForm({ ...form, color: v })}
                placeholder="#RRGGBB"
              />
            </div>
          </div>
          <div className="row">
            <Switch
              on={form.is_active}
              onChange={(v) => setForm({ ...form, is_active: v })}
              label="Active"
            />
            <span className="muted">Active</span>
          </div>
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)', borderBottom: 'none' }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
